import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import type { Session } from '@supabase/supabase-js'
import {
  ArrowRight,
  Bell,
  Check,
  ChevronDown,
  CircleGauge,
  Clock3,
  Command,
  FileText,
  FlaskConical,
  Layers3,
  Lightbulb,
  LogOut,
  Menu,
  Package2,
  PanelLeftClose,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  X,
} from 'lucide-react'
import { supabase } from './lib/supabase'
import { demoAssets, demoEvaluations, demoEvents, demoIdeas, demoProducts, demoResearch } from './demo'
import type { Asset, Decision, Evaluation, EvidenceGrade, Idea, IdeaStatus, Product, ResearchEntry, WorkflowEvent } from './types'
import {
  buildIdeaBriefUpdate,
  decisionTargets,
  getDecisionReadinessError,
  getTransitionBlocker,
  isIdeaBriefComplete,
  validateDecision,
  validTransitions,
} from './workflow'

const statusLabel: Record<IdeaStatus, string> = {
  new: 'New',
  researching: 'Researching',
  research_complete: 'Research complete',
  council_review: 'Council review',
  awaiting_ceo: 'CEO decision',
  ceo_approved: 'Approved',
  revision_requested: 'Revision requested',
  on_hold: 'On hold',
  rejected: 'Rejected',
  design: 'Design',
  creative_review: 'Creative review',
  ready_for_test: 'Ready for test',
  market_test: 'Market test',
  validated: 'Validated',
  scale: 'Scale',
  retired: 'Retired',
}

const stages: { name: string; statuses: IdeaStatus[]; tone: string }[] = [
  { name: 'Discover', statuses: ['new', 'researching', 'research_complete'], tone: 'sky' },
  { name: 'Evaluate', statuses: ['council_review', 'awaiting_ceo'], tone: 'violet' },
  { name: 'Make', statuses: ['ceo_approved', 'design', 'creative_review'], tone: 'orange' },
  { name: 'Test', statuses: ['ready_for_test', 'market_test'], tone: 'blue' },
  { name: 'Scale', statuses: ['validated', 'scale'], tone: 'green' },
]

const demoMode = import.meta.env.DEV && new URLSearchParams(window.location.search).has('demo')

function timeAgo(value: string) {
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function initials(email?: string) {
  if (!email) return 'NC'
  return email.split('@')[0].split(/[._-]/).map((part) => part[0]?.toUpperCase()).join('').slice(0, 2) || 'CEO'
}

function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setMessage('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setMessage(error.message)
    setBusy(false)
  }

  const sendMagicLink = async () => {
    if (!email) {
      setMessage('Enter your email first.')
      return
    }
    setBusy(true)
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
        shouldCreateUser: false,
      },
    })
    setMessage(error ? error.message : 'Check your inbox for a secure sign-in link.')
    setBusy(false)
  }

  return (
    <main className="login-shell">
      <section className="login-story">
        <div className="brand-lockup brand-lockup--light"><span>TL</span><b>TLife</b></div>
        <div className="login-thesis">
          <p className="eyebrow">The product company system</p>
          <h1>Build the thing. <em>Prove it.</em> Do it again.</h1>
          <p>The operating system for turning raw hunches into products people actually want.</p>
        </div>
        <div className="signal-line" aria-hidden="true">
          {[42, 68, 51, 76, 61, 88, 72, 96].map((height, index) => <i key={index} style={{ height: `${height}%` }} />)}
        </div>
        <p className="login-footnote"><ShieldCheck size={16} /> Owner-scoped data · Protected by Supabase RLS</p>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <p className="eyebrow">Private workspace</p>
          <h2>Welcome back.</h2>
          <p className="muted">Sign in to open your CEO desk.</p>
          <form onSubmit={submit}>
            <label>Email address<input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" /></label>
            <label>Password<input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" /></label>
            {message && <p className="form-message">{message}</p>}
            <button className="primary-button full-width" disabled={busy}>{busy ? 'Opening…' : 'Open CEO desk'} <ArrowRight size={17} /></button>
          </form>
          <button className="text-button" onClick={sendMagicLink} disabled={busy}>Email me a sign-in link</button>
        </div>
      </section>
    </main>
  )
}

type NewIdeaModalProps = { onClose: () => void; onCreated: () => void }

function NewIdeaModal({ onClose, onCreated }: NewIdeaModalProps) {
  const [title, setTitle] = useState('')
  const [rawIdea, setRawIdea] = useState('')
  const [priority, setPriority] = useState('3')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (demoMode) { onCreated(); return }
    setBusy(true)
    const result = await supabase.from('ideas').insert({ title, raw_idea: rawIdea, ceo_priority: Number(priority), status: 'new', origin: 'ceo' })
    if (result.error) setError(result.error.message)
    else onCreated()
    setBusy(false)
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="new-idea-title">
        <button className="icon-button modal-close" onClick={onClose} aria-label="Close"><X size={19} /></button>
        <p className="eyebrow">Capture the signal</p>
        <h2 id="new-idea-title">Add a product idea</h2>
        <p className="muted">Keep it rough. The system will help sharpen it.</p>
        <form onSubmit={submit}>
          <label>Working title<input autoFocus required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sunday Reset Planner" /></label>
          <label>The raw idea<textarea required rows={5} value={rawIdea} onChange={(e) => setRawIdea(e.target.value)} placeholder="What is it, who is it for, and why might they care?" /></label>
          <label>CEO priority<select value={priority} onChange={(e) => setPriority(e.target.value)}><option value="1">1 — Immediate</option><option value="2">2 — High</option><option value="3">3 — Normal</option><option value="4">4 — Watch</option><option value="5">5 — Backlog</option></select></label>
          {error && <p className="form-message">{error}</p>}
          <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button className="primary-button" disabled={busy}>{busy ? 'Adding…' : 'Add to pipeline'} <ArrowRight size={16} /></button></div>
        </form>
      </section>
    </div>
  )
}

type IdeaWorkspaceProps = {
  idea: Idea
  onClose: () => void
  onChanged: () => Promise<void>
}

function IdeaWorkspace({ idea, onClose, onChanged }: IdeaWorkspaceProps) {
  const [form, setForm] = useState({
    title: idea.title ?? '',
    raw_idea: idea.raw_idea,
    buyer_summary: idea.buyer_summary ?? '',
    primary_niche: idea.primary_niche ?? '',
    emotional_driver: idea.emotional_driver ?? '',
    recommended_format: idea.recommended_format ?? '',
    ceo_priority: String(idea.ceo_priority ?? 3),
  })
  const [research, setResearch] = useState<ResearchEntry[]>(demoMode ? demoResearch.filter((item) => item.idea_id === idea.id) : [])
  const [evaluations, setEvaluations] = useState<Evaluation[]>(demoMode ? demoEvaluations.filter((item) => item.idea_id === idea.id) : [])
  const [decisions, setDecisions] = useState<Decision[]>([])
  const [nextStatus, setNextStatus] = useState<IdeaStatus | ''>('')
  const [rationale, setRationale] = useState('')
  const [loadingDetails, setLoadingDetails] = useState(!demoMode)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [researchFormOpen, setResearchFormOpen] = useState(false)
  const [evaluationFormOpen, setEvaluationFormOpen] = useState(false)
  const [researchForm, setResearchForm] = useState({
    research_type: 'buyer_signal',
    title: '',
    summary: '',
    source_name: '',
    source_url: '',
    confidence: 'experimental' as EvidenceGrade,
  })
  const [evaluationForm, setEvaluationForm] = useState({
    evaluator_role: '',
    overall_score: '',
    evidence_grade: 'experimental' as EvidenceGrade,
    recommendation: '',
    critique: '',
  })

  const loadDetails = useCallback(async () => {
    if (demoMode) return
    setLoadingDetails(true)
    const [researchResult, evaluationsResult, decisionsResult] = await Promise.all([
      supabase.from('research').select('id, idea_id, research_type, title, summary, source_url, source_name, confidence, created_at').eq('idea_id', idea.id).order('created_at', { ascending: false }),
      supabase.from('evaluations').select('id, idea_id, evaluator_role, overall_score, evidence_grade, recommendation, critique, created_at').eq('idea_id', idea.id).order('created_at', { ascending: false }),
      supabase.from('decisions').select('id, idea_id, decision, rationale, decided_at').eq('idea_id', idea.id).order('decided_at', { ascending: false }),
    ])
    setResearch((researchResult.data ?? []) as ResearchEntry[])
    setEvaluations((evaluationsResult.data ?? []) as Evaluation[])
    setDecisions((decisionsResult.data ?? []) as Decision[])
    const error = researchResult.error || evaluationsResult.error || decisionsResult.error
    if (error) setMessage(`Could not load the full dossier: ${error.message}`)
    setLoadingDetails(false)
  }, [idea.id])

  useEffect(() => { loadDetails() }, [loadDetails])

  useEffect(() => {
    setForm({
      title: idea.title ?? '',
      raw_idea: idea.raw_idea,
      buyer_summary: idea.buyer_summary ?? '',
      primary_niche: idea.primary_niche ?? '',
      emotional_driver: idea.emotional_driver ?? '',
      recommended_format: idea.recommended_format ?? '',
      ceo_priority: String(idea.ceo_priority ?? 3),
    })
    setNextStatus('')
  }, [idea])

  const saveBrief = async (event: FormEvent) => {
    event.preventDefault()
    if (demoMode) { setMessage('Preview brief updated for this session.'); return }
    setBusy(true)
    setMessage('')
    const { error } = await supabase.from('ideas').update(buildIdeaBriefUpdate(form)).eq('id', idea.id)
    setMessage(error ? error.message : 'Brief saved.')
    if (!error) await onChanged()
    setBusy(false)
  }

  const moveStage = async () => {
    if (!nextStatus) return
    const blocker = getTransitionBlocker(idea.status, nextStatus, readiness)
    if (blocker) {
      setMessage(blocker)
      return
    }
    if (demoMode) { setMessage(`Preview moved to ${statusLabel[nextStatus]}.`); return }
    setBusy(true)
    setMessage('')
    const { error } = await supabase.from('ideas').update({ status: nextStatus }).eq('id', idea.id)
    setMessage(error ? error.message : `Moved to ${statusLabel[nextStatus]}.`)
    if (!error) await onChanged()
    setBusy(false)
  }

  const addResearch = async (event: FormEvent) => {
    event.preventDefault()
    if (!researchForm.title.trim() || !researchForm.summary.trim()) {
      setMessage('Name the signal and write what it means before saving it.')
      return
    }

    const entry = {
      idea_id: idea.id,
      research_type: researchForm.research_type,
      title: researchForm.title.trim(),
      summary: researchForm.summary.trim(),
      source_name: researchForm.source_name.trim() || null,
      source_url: researchForm.source_url.trim() || null,
      confidence: researchForm.confidence,
    }

    if (demoMode) {
      setResearch((items) => [{ ...entry, id: `demo-research-${Date.now()}`, created_at: new Date().toISOString() }, ...items])
      setResearchFormOpen(false)
      setResearchForm({ research_type: 'buyer_signal', title: '', summary: '', source_name: '', source_url: '', confidence: 'experimental' })
      setMessage('Preview research note saved for this session.')
      return
    }

    setBusy(true)
    setMessage('')
    const { error } = await supabase.from('research').insert(entry)
    if (error) {
      setMessage(error.message)
      setBusy(false)
      return
    }

    const { error: logError } = await supabase.from('workflow_events').insert({
      idea_id: idea.id,
      owner_id: idea.owner_id,
      event_type: 'research_added',
      note: `Research added: ${entry.title}`,
    })
    await Promise.all([loadDetails(), onChanged()])
    setResearchFormOpen(false)
    setResearchForm({ research_type: 'buyer_signal', title: '', summary: '', source_name: '', source_url: '', confidence: 'experimental' })
    setMessage(logError ? 'Research saved. The activity log could not be updated.' : 'Research note saved.')
    setBusy(false)
  }

  const addEvaluation = async (event: FormEvent) => {
    event.preventDefault()
    const score = Number(evaluationForm.overall_score)
    if (!evaluationForm.evaluator_role.trim() || !evaluationForm.recommendation.trim()) {
      setMessage('Name the reviewer and record a recommendation before saving it.')
      return
    }
    if (!Number.isFinite(score) || score < 0 || score > 100) {
      setMessage('Overall score must be a number from 0 to 100.')
      return
    }

    const entry = {
      idea_id: idea.id,
      evaluator_role: evaluationForm.evaluator_role.trim(),
      overall_score: score,
      evidence_grade: evaluationForm.evidence_grade,
      recommendation: evaluationForm.recommendation.trim(),
      critique: evaluationForm.critique.trim() || null,
    }

    if (demoMode) {
      setEvaluations((items) => [{ ...entry, id: `demo-evaluation-${Date.now()}`, created_at: new Date().toISOString() }, ...items])
      setEvaluationFormOpen(false)
      setEvaluationForm({ evaluator_role: '', overall_score: '', evidence_grade: 'experimental', recommendation: '', critique: '' })
      setMessage('Preview council readout saved for this session.')
      return
    }

    setBusy(true)
    setMessage('')
    const { error } = await supabase.from('evaluations').insert(entry)
    if (error) {
      setMessage(error.message)
      setBusy(false)
      return
    }

    const { error: logError } = await supabase.from('workflow_events').insert({
      idea_id: idea.id,
      owner_id: idea.owner_id,
      event_type: 'evaluation_added',
      note: `${entry.evaluator_role} scored the opportunity at ${entry.overall_score}.`,
    })
    await Promise.all([loadDetails(), onChanged()])
    setEvaluationFormOpen(false)
    setEvaluationForm({ evaluator_role: '', overall_score: '', evidence_grade: 'experimental', recommendation: '', critique: '' })
    setMessage(logError ? 'Evaluation saved. The activity log could not be updated.' : 'Council readout saved.')
    setBusy(false)
  }

  const makeDecision = async (decision: Decision['decision']) => {
    const validationError = validateDecision(idea.status, rationale, readiness)
    if (validationError) {
      setMessage(validationError)
      return
    }
    const cleanRationale = rationale.trim()
    const target = decisionTargets[decision]
    if (demoMode) { setMessage(`Preview decision recorded: ${decision}.`); return }
    setBusy(true)
    setMessage('')
    const decisionResult = await supabase.from('decisions').insert({ idea_id: idea.id, decision, rationale: cleanRationale })
    if (decisionResult.error) {
      setMessage(decisionResult.error.message)
      setBusy(false)
      return
    }
    const updateResult = await supabase.from('ideas').update({ status: target }).eq('id', idea.id)
    setMessage(updateResult.error ? updateResult.error.message : `${statusLabel[target]}. Decision and rationale locked.`)
    if (!updateResult.error) {
      setRationale('')
      await Promise.all([onChanged(), loadDetails()])
    }
    setBusy(false)
  }

  const transitions = validTransitions[idea.status]
  const readiness = {
    briefComplete: isIdeaBriefComplete({
      title: idea.title ?? '',
      raw_idea: idea.raw_idea,
      buyer_summary: idea.buyer_summary ?? '',
      primary_niche: idea.primary_niche ?? '',
      emotional_driver: idea.emotional_driver ?? '',
      recommended_format: idea.recommended_format ?? '',
    }),
    researchCount: research.length,
    evaluationCount: evaluations.length,
  }
  const readinessItems = [
    { label: 'Brief locked', note: 'buyer · niche · emotion · format', complete: readiness.briefComplete },
    { label: 'Signal logged', note: `${readiness.researchCount} research note${readiness.researchCount === 1 ? '' : 's'}`, complete: readiness.researchCount > 0 },
    { label: 'Council heard', note: `${readiness.evaluationCount} readout${readiness.evaluationCount === 1 ? '' : 's'}`, complete: readiness.evaluationCount > 0 },
  ]
  const selectedBlocker = nextStatus ? getTransitionBlocker(idea.status, nextStatus, readiness) : null
  const decisionReadinessError = getDecisionReadinessError(readiness)
  const decisionReady = idea.status === 'awaiting_ceo' && !decisionReadinessError

  return (
    <div className="modal-backdrop workspace-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="idea-workspace" role="dialog" aria-modal="true" aria-labelledby="workspace-title">
        <header className="workspace-header">
          <div>
            <p className="eyebrow">Idea {String(idea.idea_number).padStart(3, '0')} // working dossier</p>
            <h2 id="workspace-title">{idea.title || 'Untitled opportunity'}</h2>
          </div>
          <div className="workspace-header-actions"><span className={`status-pill status-${idea.status}`}>{statusLabel[idea.status]}</span><button className="icon-button" onClick={onClose} aria-label="Close idea workspace"><X size={20} /></button></div>
        </header>

        <div className="workspace-layout">
          <form className="workspace-brief" onSubmit={saveBrief}>
            <div className="workspace-section-heading"><div><p className="eyebrow">01 // sharpen the signal</p><h3>Product brief</h3></div><button className="primary-button" disabled={busy}>{busy ? 'Saving…' : 'Save brief'}</button></div>
            <div className="workspace-fields">
              <label className="field-wide">Working title<input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
              <label className="field-wide">Raw idea<textarea required rows={5} value={form.raw_idea} onChange={(event) => setForm({ ...form, raw_idea: event.target.value })} /></label>
              <label className="field-wide">Who buys this, and why?<textarea rows={3} value={form.buyer_summary} onChange={(event) => setForm({ ...form, buyer_summary: event.target.value })} placeholder="A specific person, problem, and buying trigger." /></label>
              <label>Primary niche<input value={form.primary_niche} onChange={(event) => setForm({ ...form, primary_niche: event.target.value })} placeholder="e.g. Founder productivity" /></label>
              <label>Core emotion<input value={form.emotional_driver} onChange={(event) => setForm({ ...form, emotional_driver: event.target.value })} placeholder="e.g. Relief" /></label>
              <label>Recommended format<input value={form.recommended_format} onChange={(event) => setForm({ ...form, recommended_format: event.target.value })} placeholder="e.g. Guided workbook" /></label>
              <label>CEO priority<select value={form.ceo_priority} onChange={(event) => setForm({ ...form, ceo_priority: event.target.value })}><option value="1">1 — Immediate</option><option value="2">2 — High</option><option value="3">3 — Normal</option><option value="4">4 — Watch</option><option value="5">5 — Backlog</option></select></label>
            </div>
          </form>

          <aside className="workspace-rail">
            <section className="gate-card">
              <p className="eyebrow">02 // move the work</p><h3>Stage gate</h3>
              <div className="gate-current"><span>Now</span><b>{statusLabel[idea.status]}</b></div>
              <div className="readiness-ticket" aria-label="Decision readiness checklist">
                <div className="readiness-ticket-head"><span>Decision readiness</span><b>{readinessItems.filter((item) => item.complete).length}/3</b></div>
                {readinessItems.map((item) => <div className={`readiness-item ${item.complete ? 'readiness-item--done' : ''}`} key={item.label}><span aria-hidden="true">{item.complete ? <Check size={12} /> : '→'}</span><div><b>{item.label}</b><small>{item.note}</small></div></div>)}
              </div>
              {transitions.length ? <div className="gate-controls"><select aria-label="Next stage" value={nextStatus} onChange={(event) => setNextStatus(event.target.value as IdeaStatus)}><option value="">Choose the next gate</option>{transitions.map((status) => <option key={status} value={status}>{statusLabel[status]}</option>)}</select>{selectedBlocker && <p className="gate-blocker" role="note">{selectedBlocker}</p>}<button className="secondary-button full-width" type="button" disabled={busy || loadingDetails || !nextStatus || Boolean(selectedBlocker)} onClick={moveStage}>Move idea <ArrowRight size={15} /></button></div> : <p className="rail-note">This idea has reached the end of the current track.</p>}
            </section>

            <section className="evidence-card" id="research">
              <p className="eyebrow">03 // evidence board</p><div className="evidence-title-row"><h3>Research <span>{research.length}</span></h3><button className="capture-toggle" type="button" onClick={() => setResearchFormOpen((value) => !value)}>{researchFormOpen ? 'Close' : '+ Add note'}</button></div>
              {researchFormOpen && <form className="capture-form" onSubmit={addResearch}>
                <label>Signal label<input autoFocus required value={researchForm.title} onChange={(event) => setResearchForm({ ...researchForm, title: event.target.value })} placeholder="What did you learn?" /></label>
                <label>What it says<textarea required rows={3} value={researchForm.summary} onChange={(event) => setResearchForm({ ...researchForm, summary: event.target.value })} placeholder="State the signal, not the spin." /></label>
                <div className="capture-form-grid"><label>Evidence type<select value={researchForm.research_type} onChange={(event) => setResearchForm({ ...researchForm, research_type: event.target.value })}><option value="buyer_signal">Buyer signal</option><option value="demand_signal">Demand signal</option><option value="competitor_scan">Competitor scan</option><option value="customer_note">Customer note</option><option value="test_result">Test result</option><option value="other">Other</option></select></label><label>Confidence<select value={researchForm.confidence} onChange={(event) => setResearchForm({ ...researchForm, confidence: event.target.value as EvidenceGrade })}><option value="experimental">Experimental</option><option value="d">D — weak</option><option value="c">C — directional</option><option value="b">B — solid</option><option value="a">A — strong</option></select></label></div>
                <label>Source name<input value={researchForm.source_name} onChange={(event) => setResearchForm({ ...researchForm, source_name: event.target.value })} placeholder="Interview, report, test…" /></label>
                <label>Source URL<input type="url" value={researchForm.source_url} onChange={(event) => setResearchForm({ ...researchForm, source_url: event.target.value })} placeholder="https:// (optional)" /></label>
                <div className="capture-actions"><button type="button" className="text-button" onClick={() => setResearchFormOpen(false)}>Cancel</button><button className="secondary-button" disabled={busy}>{busy ? 'Saving…' : 'Save note'}</button></div>
              </form>}
              {loadingDetails ? <p className="rail-note">Loading the file…</p> : research.length ? <div className="dossier-list">{research.slice(0, 5).map((item) => <article key={item.id}><b>{item.title || item.research_type.replaceAll('_', ' ')}</b><p>{item.summary}</p>{item.source_url && <a href={item.source_url} target="_blank" rel="noreferrer">Open source <ArrowRight size={12} /></a>}</article>)}</div> : !researchFormOpen && <p className="rail-note">No research yet. Capture the first buyer, demand, or test signal here.</p>}
            </section>

            <section className="evidence-card">
              <p className="eyebrow">04 // council readout</p><div className="evidence-title-row"><h3>Evaluations <span>{evaluations.length}</span></h3><button className="capture-toggle" type="button" onClick={() => setEvaluationFormOpen((value) => !value)}>{evaluationFormOpen ? 'Close' : '+ Add readout'}</button></div>
              {evaluationFormOpen && <form className="capture-form" onSubmit={addEvaluation}>
                <label>Reviewer role<input autoFocus required value={evaluationForm.evaluator_role} onChange={(event) => setEvaluationForm({ ...evaluationForm, evaluator_role: event.target.value })} placeholder="Market skeptic, buyer advocate…" /></label>
                <div className="capture-form-grid"><label>Overall score<input required type="number" min="0" max="100" inputMode="numeric" value={evaluationForm.overall_score} onChange={(event) => setEvaluationForm({ ...evaluationForm, overall_score: event.target.value })} placeholder="0–100" /></label><label>Evidence grade<select value={evaluationForm.evidence_grade} onChange={(event) => setEvaluationForm({ ...evaluationForm, evidence_grade: event.target.value as EvidenceGrade })}><option value="experimental">Experimental</option><option value="d">D — weak</option><option value="c">C — directional</option><option value="b">B — solid</option><option value="a">A — strong</option></select></label></div>
                <label>Recommendation<textarea required rows={3} value={evaluationForm.recommendation} onChange={(event) => setEvaluationForm({ ...evaluationForm, recommendation: event.target.value })} placeholder="Advance, revise, hold, or reject—and why?" /></label>
                <label>Hard truth<textarea rows={2} value={evaluationForm.critique} onChange={(event) => setEvaluationForm({ ...evaluationForm, critique: event.target.value })} placeholder="What could kill this? (optional)" /></label>
                <div className="capture-actions"><button type="button" className="text-button" onClick={() => setEvaluationFormOpen(false)}>Cancel</button><button className="secondary-button" disabled={busy}>{busy ? 'Saving…' : 'Save readout'}</button></div>
              </form>}
              {evaluations.length ? <div className="score-list">{evaluations.slice(0, 5).map((item) => <article key={item.id}><span>{item.evaluator_role}</span><b>{item.overall_score ?? '—'}<small>/100</small></b><p>{item.recommendation || item.critique || 'No written recommendation.'}</p></article>)}</div> : !evaluationFormOpen && <p className="rail-note">No council evaluations yet. Add a human readout before AI council automation arrives.</p>}
            </section>
          </aside>
        </div>

        <section className={`decision-desk ${decisionReady ? 'decision-desk--ready' : ''}`}>
          <div className="decision-desk-copy"><p className="eyebrow">05 // founder call</p><h3>{decisionReady ? 'Make the call' : 'Decision desk is gated'}</h3><p>{decisionReady ? 'The reason matters as much as the answer. It becomes part of the company memory.' : idea.status === 'awaiting_ceo' ? decisionReadinessError : `Advance through the valid stage gates until this reaches ${statusLabel.awaiting_ceo}.`}</p></div>
          <div className="decision-entry"><label>Decision rationale<textarea rows={3} value={rationale} onChange={(event) => setRationale(event.target.value)} placeholder="What evidence or constraint made this the right call?" disabled={!decisionReady} /></label><div className="decision-buttons"><button className="approve-button" type="button" disabled={busy || !decisionReady} onClick={() => makeDecision('approve')}><Check size={16} />Approve</button><button className="secondary-button" type="button" disabled={busy || !decisionReady} onClick={() => makeDecision('revise')}>Revise</button><button className="secondary-button" type="button" disabled={busy || !decisionReady} onClick={() => makeDecision('hold')}>Hold</button><button className="reject-button" type="button" disabled={busy || !decisionReady} onClick={() => makeDecision('reject')}>Reject</button></div></div>
          <div className="decision-history"><b>Decision log</b>{decisions.length ? decisions.slice(0, 4).map((item) => <article key={item.id}><span>{item.decision}</span><p>{item.rationale || 'No rationale recorded.'}</p><small>{new Date(item.decided_at).toLocaleDateString()}</small></article>) : <p className="rail-note">No calls recorded yet.</p>}</div>
        </section>
        {message && <p className="workspace-message" role="status">{message}</p>}
      </section>
    </div>
  )
}

type DashboardProps = {
  session: Session | null
  ideas: Idea[]
  events: WorkflowEvent[]
  products: Product[]
  research: ResearchEntry[]
  evaluations: Evaluation[]
  assets: Asset[]
  loading: boolean
  loadError: string
  onRefresh: () => Promise<void>
}

function Dashboard({ session, ideas, events, products, research, evaluations, assets, loading, loadError, onRefresh }: DashboardProps) {
  const [mobileNav, setMobileNav] = useState(false)
  const [newIdeaOpen, setNewIdeaOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [workspaceOpen, setWorkspaceOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showAllEvents, setShowAllEvents] = useState(false)

  const selectedIdea = useMemo(() => {
    const chosen = ideas.find((idea) => idea.id === selectedId)
    return chosen ?? ideas.find((idea) => idea.status === 'awaiting_ceo') ?? ideas[0]
  }, [ideas, selectedId])

  const pipelineCount = ideas.filter((idea) => !['rejected', 'retired'].includes(idea.status)).length
  const waitingCount = ideas.filter((idea) => idea.status === 'awaiting_ceo').length
  const averageScore = Math.round(ideas.reduce((sum, idea) => sum + (Number(idea.opportunity_score) || 0), 0) / Math.max(ideas.filter((idea) => idea.opportunity_score != null).length, 1))
  const testedCount = ideas.filter((idea) => ['market_test', 'validated', 'scale'].includes(idea.status)).length
  const validatedRate = testedCount ? Math.round((ideas.filter((idea) => ['validated', 'scale'].includes(idea.status)).length / testedCount) * 100) : 0
  const upcoming = [...products].filter((product) => product.launch_date).sort((a, b) => String(a.launch_date).localeCompare(String(b.launch_date)))[0]
  const visibleEvents = showAllEvents ? events : events.slice(0, 5)
  const searchResults = useMemo(() => {
    const term = searchQuery.trim().toLowerCase()
    const matchingIdeas = ideas.filter((idea) => !term || [idea.title, idea.raw_idea, idea.primary_niche, idea.buyer_summary].some((value) => value?.toLowerCase().includes(term))).slice(0, 6)
    const matchingProducts = products.filter((product) => !term || [product.name, product.status].some((value) => value.toLowerCase().includes(term))).slice(0, 4)
    return { ideas: matchingIdeas, products: matchingProducts }
  }, [ideas, products, searchQuery])

  const todayLabel = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date())
  const openWorkspace = (ideaId?: string) => {
    if (ideaId) setSelectedId(ideaId)
    setSearchOpen(false)
    setSearchQuery('')
    setWorkspaceOpen(true)
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearchOpen(true)
      }
      if (event.key === 'Escape') setSearchOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? 'sidebar--open' : ''}`}>
        <div className="brand-lockup"><span>TL</span><b>TLife</b><button className="icon-button nav-close" onClick={() => setMobileNav(false)} aria-label="Close menu"><PanelLeftClose size={19} /></button></div>
        <nav aria-label="Primary navigation">
          <a className="nav-item active" href="#overview" onClick={() => setMobileNav(false)}><CircleGauge size={18} />Overview</a>
          <a className="nav-item" href="#pipeline" onClick={() => setMobileNav(false)}><Layers3 size={18} />Idea pipeline <span>{pipelineCount}</span></a>
          <a className="nav-item" href="#decisions" onClick={() => setMobileNav(false)}><Target size={18} />Decisions <span className={waitingCount ? 'alert-count' : ''}>{waitingCount}</span></a>
          <a className="nav-item" href="#products" onClick={() => setMobileNav(false)}><Package2 size={18} />Products</a>
          <a className="nav-item" href="#research-center" onClick={() => setMobileNav(false)}><FlaskConical size={18} />Research</a>
        </nav>
        <div className="nav-section">
          <p>System</p>
          <a className="nav-item" href="#research-center" onClick={() => setMobileNav(false)}><Users size={18} />Council & agents</a>
          <a className="nav-item" href="#research-center" onClick={() => setMobileNav(false)}><FileText size={18} />Documents</a>
        </div>
        <div className="sidebar-status">
          <div><i /><span><b>System online</b><small>Supabase + Cloudflare</small></span></div>
          {demoMode && <em>Preview data</em>}
        </div>
      </aside>

      <main className="main-content" id="overview">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileNav(true)} aria-label="Open menu"><Menu size={20} /></button>
          <button className="command-search" onClick={() => setSearchOpen(true)}><Search size={17} /><span>Search the company</span><kbd><Command size={12} /> K</kbd></button>
          <div className="topbar-actions">
            <button className="icon-button" aria-label={`${waitingCount} decisions need attention`} onClick={() => document.getElementById('decisions')?.scrollIntoView({ behavior: 'smooth' })}><Bell size={19} />{waitingCount > 0 && <i />}</button>
            <div className="user-chip"><span>{initials(session?.user.email)}</span><div><b>{session?.user.user_metadata?.full_name || 'CEO workspace'}</b><small>{session?.user.email || 'Preview mode'}</small></div><ChevronDown size={14} /></div>
            {!demoMode && <button className="icon-button" onClick={() => supabase.auth.signOut()} aria-label="Sign out"><LogOut size={18} /></button>}
          </div>
        </header>

        <div className="garage-ticker" aria-label="Operating principles">
          <div>
            <span>Customer signal &gt; founder ego</span><b>✦</b>
            <span>Make the call</span><b>✦</b>
            <span>Ship small. Learn loud.</span><b>✦</b>
            <span>No theatre. Just traction.</span><b>✦</b>
            <span>Customer signal &gt; founder ego</span><b>✦</b>
            <span>Make the call</span><b>✦</b>
            <span>Ship small. Learn loud.</span><b>✦</b>
            <span>No theatre. Just traction.</span><b>✦</b>
          </div>
        </div>

        <div className="content-wrap">
          <div className="garage-mess" aria-hidden="true">
            <i className="tea-ring tea-ring--top" />
            <i className="tea-drop tea-drop--one" />
            <i className="tea-drop tea-drop--two" />
            <i className="tea-ring tea-ring--lower" />
            <div className="crumb-field crumb-field--top">
              {Array.from({ length: 9 }).map((_, index) => <i key={`top-${index}`} />)}
            </div>
            <div className="crumb-field crumb-field--lower">
              {Array.from({ length: 7 }).map((_, index) => <i key={`lower-${index}`} />)}
            </div>
          </div>
          <section className="page-heading">
            <div><p className="eyebrow">Garage control // {todayLabel}</p><h1>Build fast.<br/><span>Bet smart.</span></h1><p>Your product company—no theatre, just signal, decisions, and momentum.</p></div>
            <button className="primary-button" onClick={() => setNewIdeaOpen(true)}><Plus size={17} />Add idea</button>
          </section>

          {loadError && <section className="load-failure" role="alert"><div><b>THE DATA LINE DROPPED.</b><p>{loadError}</p></div><button className="secondary-button" onClick={() => onRefresh()}>Try again</button></section>}
          {loading ? <div className="loading-bar" /> : (
            <>
              <section className="metric-row" aria-label="Company snapshot">
                <article><div className="metric-icon blue"><Layers3 size={19} /></div><p>Active pipeline</p><strong>{pipelineCount}</strong><small><TrendingUp size={14} /> {ideas.filter((idea) => new Date(idea.created_at).getTime() > Date.now() - 7 * 86_400_000).length} added this week</small></article>
                <article className={waitingCount ? 'metric-urgent' : ''}><div className="metric-icon coral"><Target size={19} /></div><p>Needs your decision</p><strong>{waitingCount}</strong><small><Clock3 size={14} /> {waitingCount ? 'Highest leverage work' : 'Desk is clear'}</small></article>
                <article><div className="metric-icon violet"><Sparkles size={19} /></div><p>Opportunity quality</p><strong>{averageScore || '—'}<sup>{averageScore ? '/100' : ''}</sup></strong><small>{averageScore >= 80 ? 'Strong portfolio signal' : 'Awaiting evaluated ideas'}</small></article>
                <article><div className="metric-icon green"><Check size={19} /></div><p>Validation rate</p><strong>{validatedRate}%</strong><small>{testedCount} market-tested concept{testedCount === 1 ? '' : 's'}</small></article>
              </section>

              <section className="decision-grid" id="decisions">
                <article className="decision-card">
                  <div className="section-heading"><div><p className="eyebrow">Make the call</p><h2>Your next move</h2></div><span className="live-badge"><i />Live priority</span></div>
                  {selectedIdea ? (
                    <div className="decision-body">
                      <div className="idea-meta"><span>IDEA {String(selectedIdea.idea_number).padStart(3, '0')}</span><span className={`status-pill status-${selectedIdea.status}`}>{statusLabel[selectedIdea.status]}</span><span>Priority {selectedIdea.ceo_priority ?? '—'}</span></div>
                      <h3>{selectedIdea.title || 'Untitled opportunity'}</h3>
                      <p className="idea-thesis">{selectedIdea.buyer_summary || selectedIdea.raw_idea}</p>
                      <div className="evidence-strip">
                        <div><small>Opportunity</small><b>{selectedIdea.opportunity_score ?? '—'}<em>{selectedIdea.opportunity_score ? '/100' : ''}</em></b></div>
                        <div><small>Evidence</small><b>{selectedIdea.evidence_grade?.toUpperCase() ?? '—'}</b></div>
                        <div><small>Format</small><b>{selectedIdea.recommended_format || 'TBD'}</b></div>
                        <div><small>Core emotion</small><b>{selectedIdea.emotional_driver || 'In research'}</b></div>
                      </div>
                      <div className="decision-actions">
                        <button className="approve-button" onClick={() => openWorkspace()}><FileText size={17} />Open working dossier</button>
                        <span className="decision-hint">Edit the brief, move gates, and record the reason behind the call.</span>
                      </div>
                    </div>
                  ) : <div className="empty-state"><Lightbulb size={24} /><h3>No ideas yet</h3><p>Add the first signal to start the product system.</p></div>}
                </article>

                <article className="activity-card">
                  <div className="section-heading"><div><p className="eyebrow">Signal log</p><h2>What moved</h2></div>{events.length > 5 && <button className="text-button" onClick={() => setShowAllEvents((value) => !value)}>{showAllEvents ? 'Show latest' : `View all ${events.length}`}</button>}</div>
                  <div className="activity-list">
                    {visibleEvents.map((event) => {
                      const idea = ideas.find((item) => item.id === event.idea_id)
                      return <button key={event.id} onClick={() => openWorkspace(event.idea_id)}><span className="activity-mark"><i /></span><div><b>{idea?.title || `Idea ${idea?.idea_number ?? ''}`}</b><p>{event.note || event.event_type.replaceAll('_', ' ')}</p><small>{timeAgo(event.created_at)}</small></div></button>
                    })}
                    {!events.length && <div className="empty-activity">Workflow events will appear here.</div>}
                  </div>
                </article>
              </section>

              <section className="pipeline-card" id="pipeline">
                <div className="section-heading"><div><p className="eyebrow">The shop floor</p><h2>From hunch to hit</h2></div><div className="pipeline-legend"><span><i className="hot" />Needs attention</span><span><i />On course</span></div></div>
                <div className="runway">
                  {stages.map((stage, index) => {
                    const stageIdeas = ideas.filter((idea) => stage.statuses.includes(idea.status))
                    return <div className={`runway-stage tone-${stage.tone}`} key={stage.name}>
                      <div className="stage-top"><span>{String(index + 1).padStart(2, '0')}</span><b>{stage.name}</b><em>{stageIdeas.length}</em></div>
                      <div className="stage-track"><i style={{ width: `${Math.min(100, 18 + stageIdeas.length * 22)}%` }} /></div>
                      <div className="stage-items">
                        {stageIdeas.slice(0, 3).map((idea) => <button key={idea.id} className={idea.status === 'awaiting_ceo' ? 'needs-attention' : ''} onClick={() => openWorkspace(idea.id)}><span>{idea.status === 'awaiting_ceo' ? '!' : idea.idea_number}</span><p>{idea.title || 'Untitled idea'}<small>{statusLabel[idea.status]}</small></p><ArrowRight size={14} /></button>)}
                        {!stageIdeas.length && <p className="stage-empty">Clear</p>}
                      </div>
                    </div>
                  })}
                </div>
              </section>

              <section className="operations-grid" id="research-center">
                <article className="operations-card evidence-board">
                  <div className="section-heading"><div><p className="eyebrow">Evidence room</p><h2>Research & council</h2></div><span className="board-count">{research.length + evaluations.length} reads</span></div>
                  <div className="operations-summary">
                    <div><strong>{research.length}</strong><span>Research notes</span></div>
                    <div><strong>{evaluations.length}</strong><span>Council evaluations</span></div>
                    <div><strong>{evaluations.filter((item) => item.recommendation).length}</strong><span>Recommendations</span></div>
                  </div>
                  <div className="operations-list">
                    {research.slice(0, 2).map((item) => <button key={item.id} onClick={() => openWorkspace(item.idea_id)}><span className="ops-tag">Research</span><div><b>{item.title || item.research_type.replaceAll('_', ' ')}</b><p>{item.summary}</p></div><ArrowRight size={15} /></button>)}
                    {evaluations.slice(0, 2).map((item) => <button key={item.id} onClick={() => openWorkspace(item.idea_id)}><span className="ops-tag ops-tag--council">{item.overall_score ?? '—'}</span><div><b>{item.evaluator_role}</b><p>{item.recommendation || item.critique || 'Evaluation recorded.'}</p></div><ArrowRight size={15} /></button>)}
                    {!research.length && !evaluations.length && <div className="operations-empty"><FlaskConical size={21} /><div><b>The evidence room is empty.</b><p>Open an idea dossier to sharpen the brief before the research council runs.</p></div></div>}
                  </div>
                </article>

                <article className="operations-card" id="documents">
                  <div className="section-heading"><div><p className="eyebrow">Build artifacts</p><h2>Documents & assets</h2></div><span className="board-count">{assets.length} files</span></div>
                  <div className="operations-list operations-list--assets">
                    {assets.slice(0, 5).map((asset) => <button key={asset.id} onClick={() => asset.idea_id && openWorkspace(asset.idea_id)} disabled={!asset.idea_id}><span className="asset-mark"><FileText size={17} /></span><div><b>{asset.label}</b><p>{asset.asset_type.replaceAll('_', ' ')} · v{asset.version}</p></div><span className={`approval-state approval-state--${asset.approval_status}`}>{asset.approval_status.replaceAll('_', ' ')}</span></button>)}
                    {!assets.length && <div className="operations-empty"><FileText size={21} /><div><b>No build artifacts yet.</b><p>Approved ideas will collect copy, creative, files, and links here.</p></div></div>}
                  </div>
                </article>
              </section>

              <section className="bottom-grid">
                <article className="portfolio-card" id="products">
                  <div className="section-heading"><div><p className="eyebrow">Portfolio</p><h2>Products in motion</h2></div>{products[0] && <button className="text-button" onClick={() => openWorkspace(products[0].idea_id)}>Open top product <ArrowRight size={14} /></button>}</div>
                  <div className="product-list">
                    {products.slice(0, 4).map((product) => {
                      const margin = product.selling_price && product.unit_cost ? Math.round(((product.selling_price - product.unit_cost) / product.selling_price) * 100) : null
                      return <button key={product.id} onClick={() => openWorkspace(product.idea_id)}><span className="product-monogram">{product.name.slice(0, 2).toUpperCase()}</span><div><b>{product.name}</b><small>{product.status.replaceAll('_', ' ')}</small></div><p>{margin ? `${margin}% margin` : 'Margin TBD'}</p><span className="product-status">{product.launch_date ? new Date(product.launch_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'No launch date'}</span></button>
                    })}
                    {!products.length && <div className="empty-inline">Approved ideas become products here.</div>}
                  </div>
                </article>

                <article className="momentum-card">
                  <p className="eyebrow">Forward view</p><h2>{upcoming ? 'Next launch' : 'Build the launch slate'}</h2>
                  {upcoming ? <><strong>{upcoming.name}</strong><p>{new Date(upcoming.launch_date!).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</p></> : <><strong>No date set</strong><p>Move a validated product onto the calendar.</p></>}
                  <div className="momentum-visual"><span /><span /><span /><span /><span /></div>
                  <button className="secondary-button full-width" disabled={!upcoming} onClick={() => upcoming && openWorkspace(upcoming.idea_id)}>Review launch plan <ArrowRight size={15} /></button>
                </article>
              </section>
            </>
          )}
        </div>
      </main>
      {newIdeaOpen && <NewIdeaModal onClose={() => setNewIdeaOpen(false)} onCreated={async () => { setNewIdeaOpen(false); await onRefresh() }} />}
      {workspaceOpen && selectedIdea && <IdeaWorkspace idea={selectedIdea} onClose={() => setWorkspaceOpen(false)} onChanged={onRefresh} />}
      {searchOpen && <div className="search-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setSearchOpen(false)}>
        <section className="command-palette" role="dialog" aria-modal="true" aria-labelledby="company-search-title">
          <div className="command-palette-input"><Search size={20} /><label htmlFor="company-search" id="company-search-title">Search the company</label><input id="company-search" autoFocus value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Ideas, products, niches…" /><button className="icon-button" onClick={() => setSearchOpen(false)} aria-label="Close search"><X size={18} /></button></div>
          <div className="command-results">
            {searchResults.ideas.length > 0 && <div className="result-group"><p>Ideas</p>{searchResults.ideas.map((idea) => <button key={idea.id} onClick={() => openWorkspace(idea.id)}><span>{String(idea.idea_number).padStart(3, '0')}</span><div><b>{idea.title || 'Untitled opportunity'}</b><small>{statusLabel[idea.status]} · {idea.primary_niche || 'Niche open'}</small></div><ArrowRight size={15} /></button>)}</div>}
            {searchResults.products.length > 0 && <div className="result-group"><p>Products</p>{searchResults.products.map((product) => <button key={product.id} onClick={() => openWorkspace(product.idea_id)}><span className="result-product">P</span><div><b>{product.name}</b><small>{product.status.replaceAll('_', ' ')} · {product.launch_date ? `Launch ${new Date(product.launch_date).toLocaleDateString()}` : 'No launch date'}</small></div><ArrowRight size={15} /></button>)}</div>}
            {!searchResults.ideas.length && !searchResults.products.length && <div className="search-empty"><Search size={23} /><b>No signal found.</b><p>Try a product name, niche, buyer, or stage.</p></div>}
          </div>
          <footer><span><kbd>↵</kbd> Open dossier</span><span><kbd>esc</kbd> Close</span></footer>
        </section>
      </div>}
    </div>
  )
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [authReady, setAuthReady] = useState(demoMode)
  const [ideas, setIdeas] = useState<Idea[]>(demoMode ? demoIdeas : [])
  const [events, setEvents] = useState<WorkflowEvent[]>(demoMode ? demoEvents : [])
  const [products, setProducts] = useState<Product[]>(demoMode ? demoProducts : [])
  const [research, setResearch] = useState<ResearchEntry[]>(demoMode ? demoResearch : [])
  const [evaluations, setEvaluations] = useState<Evaluation[]>(demoMode ? demoEvaluations : [])
  const [assets, setAssets] = useState<Asset[]>(demoMode ? demoAssets : [])
  const [loading, setLoading] = useState(!demoMode)
  const [loadError, setLoadError] = useState('')

  const loadData = useCallback(async () => {
    if (demoMode) return
    setLoading(true)
    setLoadError('')
    try {
      const [ideasResult, eventsResult, productsResult, researchResult, evaluationsResult, assetsResult] = await Promise.all([
        supabase.from('ideas').select('*').order('ceo_priority', { ascending: true, nullsFirst: false }).order('updated_at', { ascending: false }),
        supabase.from('workflow_events').select('*').order('created_at', { ascending: false }).limit(30),
        supabase.from('products').select('id, idea_id, name, status, selling_price, unit_cost, launch_date').order('updated_at', { ascending: false }),
        supabase.from('research').select('id, idea_id, research_type, title, summary, source_url, source_name, confidence, created_at').order('created_at', { ascending: false }),
        supabase.from('evaluations').select('id, idea_id, evaluator_role, overall_score, evidence_grade, recommendation, critique, created_at').order('created_at', { ascending: false }),
        supabase.from('assets').select('id, idea_id, product_id, asset_type, label, external_url, version, approval_status, created_at').order('created_at', { ascending: false }),
      ])
      if (ideasResult.data) setIdeas(ideasResult.data as Idea[])
      if (eventsResult.data) setEvents(eventsResult.data as WorkflowEvent[])
      if (productsResult.data) setProducts(productsResult.data as Product[])
      if (researchResult.data) setResearch(researchResult.data as ResearchEntry[])
      if (evaluationsResult.data) setEvaluations(evaluationsResult.data as Evaluation[])
      if (assetsResult.data) setAssets(assetsResult.data as Asset[])
      const error = ideasResult.error || eventsResult.error || productsResult.error || researchResult.error || evaluationsResult.error || assetsResult.error
      if (error) setLoadError(`${error.message} Your existing data is still here; retry when the connection is back.`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'The company data could not be reached.'
      setLoadError(`${message} Your existing data is still here; retry when the connection is back.`)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (demoMode) return
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setAuthReady(true)
    })
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      setAuthReady(true)
    })
    return () => data.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!session || demoMode) return
    loadData()
    const channel = supabase.channel('ceo-dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ideas' }, () => loadData())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'workflow_events' }, () => loadData())
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [session, loadData])

  if (!authReady) return <div className="app-loading"><span>TL</span><p>Opening your company system…</p></div>
  if (!session && !demoMode) return <Login />
  return <Dashboard session={session} ideas={ideas} events={events} products={products} research={research} evaluations={evaluations} assets={assets} loading={loading} loadError={loadError} onRefresh={loadData} />
}
